/**
 * Rostro del jugador (retrato 2D dibujado por código y tono/pelo del modelo 3D).
 * Viaja como un código corto (`"s3h2c1b0f1e2w1m0"`), igual que el país: se
 * guarda en el navegador, en los metadatos de la cuenta (sin tocar el esquema)
 * y en la sala. El jugador elige tono, cabello, color y barba; la forma de la
 * cara, ojos, cejas y boca salen del código (con «Otra cara» se cambian).
 */
export interface FaceSpec {
  /** Tono de piel (índice de `SKIN_TONES`). */
  skin: number;
  /** Estilo de cabello (`HAIR_STYLES`). */
  hair: number;
  /** Color de cabello (`HAIR_COLORS`). */
  hairColor: number;
  /** Vello facial (`BEARDS`). */
  beard: number;
  /** Forma de la cara, ojos, cejas y boca (variedad, «personalidad»). */
  shape: number;
  eyes: number;
  brows: number;
  mouth: number;
}

export const SKIN_TONES = ['#f6d5bd', '#eec1a0', '#e0a97f', '#c98c5e', '#a96f45', '#8a5533', '#6b3f24', '#4a2a17'] as const;
export const HAIR_STYLES = ['Corto', 'Rapado', 'Rizado', 'Largo', 'Afro', 'Calvo', 'Trenzas', 'Cresta'] as const;
export const HAIR_COLORS = ['#16110d', '#3b2516', '#6b4226', '#a0692f', '#d6a756', '#8e8e8e', '#b3401f'] as const;
export const BEARDS = ['Sin barba', 'Sombra', 'Bigote', 'Perilla', 'Barba'] as const;
const SHAPES = 4;
const EYES = 4;
const BROWS = 4;
const MOUTHS = 4;

const LIMITS: Record<keyof FaceSpec, number> = {
  skin: SKIN_TONES.length,
  hair: HAIR_STYLES.length,
  hairColor: HAIR_COLORS.length,
  beard: BEARDS.length,
  shape: SHAPES,
  eyes: EYES,
  brows: BROWS,
  mouth: MOUTHS,
};
const KEYS: Array<[keyof FaceSpec, string]> = [
  ['skin', 's'],
  ['hair', 'h'],
  ['hairColor', 'c'],
  ['beard', 'b'],
  ['shape', 'f'],
  ['eyes', 'e'],
  ['brows', 'w'],
  ['mouth', 'm'],
];

export const encodeFace = (f: FaceSpec): string => KEYS.map(([k, t]) => `${t}${f[k]}`).join('');

/** Código válido → rostro (o null si no lo es: nunca confía en lo que llega). */
export const decodeFace = (raw: unknown): FaceSpec | null => {
  if (typeof raw !== 'string' || raw.length > 24) return null;
  const m = /^s(\d)h(\d)c(\d)b(\d)f(\d)e(\d)w(\d)m(\d)$/.exec(raw);
  if (!m) return null;
  const f = {} as FaceSpec;
  for (let i = 0; i < KEYS.length; i++) {
    const [k] = KEYS[i]!;
    const v = Number(m[i + 1]);
    if (v >= LIMITS[k]) return null;
    f[k] = v;
  }
  return f;
};

/** Código saneado (o null). */
export const sanitizeFace = (raw: unknown): string | null => {
  const f = decodeFace(raw);
  return f ? encodeFace(f) : null;
};

const hash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

/** Rostro estable para una semilla (los CPU: su id; el jugador sin elegir: el suyo). */
export const faceFromSeed = (seed: string): FaceSpec => {
  let h = hash(seed);
  const next = (n: number) => {
    h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 3266489909) >>> 0;
    return (h >>> 8) % n;
  };
  const skin = next(SKIN_TONES.length);
  const hair = next(HAIR_STYLES.length);
  // Canas y pelirrojos, menos frecuentes.
  const hc = next(10);
  const hairColor = hc < 4 ? 0 : hc < 6 ? 1 : hc < 7 ? 2 : hc < 8 ? 3 : hc < 9 ? 4 : next(2) ? 5 : 6;
  const beard = next(10) < 4 ? 0 : next(BEARDS.length);
  return { skin, hair, hairColor, beard, shape: next(SHAPES), eyes: next(EYES), brows: next(BROWS), mouth: next(MOUTHS) };
};

/** Rostro de un jugador: el que eligió (código) o uno estable a partir de su id. */
export const faceOf = (code: string | null | undefined, seed: string): FaceSpec => decodeFace(code) ?? faceFromSeed(seed);
